import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import RelevanceValue from '../ask/source-animated-card/relevance/relevance-badge';
import { useTranslation } from 'react-i18next';
import { tokens } from '@/locales/translations/tokens';
import FileIcon from '@/utils/file-extension-icons';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { CardData } from '../ask/source-animated-card';

interface SkeletonProps {
  className?: string;
}

const Skeleton = ({ className }: SkeletonProps) => {
  return <div className={cn("animate-pulse bg-gray-200", className)} />;
};

interface WebCardProps {
  url: string;
  domain: string;
  title: string;
  content?: string;
  relevancePercentage: number;
  idx: number;
}

const WebCard = ({
  url,
  domain,
  title,
  content = "",
  relevancePercentage,
  idx,
}: WebCardProps) => {
  const [loadedFavicons, setLoadedFavicons] = useState<Record<string, boolean>>({});
  const faviconUrl = domain ? `https://www.google.com/s2/favicons?sz=64&domain_url=${domain}` : null;

  return (
    <Card 
      key={idx} 
      className="group overflow-hidden transition-all duration-300 hover:shadow-sm px-2 rounded-md bg-background/50 hover:cursor-pointer"
    >
      <a href={url} target="_blank" rel="noopener noreferrer" className="block h-full">
        <CardHeader className="flex flex-row items-center gap-2 pb-1 p-2">
          <div className="relative h-6 w-6 overflow-hidden rounded-sm">
            {!loadedFavicons[domain] && <Skeleton className="absolute inset-0 h-full w-full" />}
            <img
              src={faviconUrl || "/placeholder.svg"}
              alt={`${domain} favicon`}
              className={`h-full w-full object-contain transition-opacity duration-300 ${loadedFavicons[domain] ? "opacity-100" : "opacity-0"}`}
              onLoad={() => setLoadedFavicons(prev => ({ ...prev, [domain]: true }))}
              onError={() => setLoadedFavicons(prev => ({ ...prev, [domain]: true }))}
            />
          </div>
          <div className="flex-1">
            <CardTitle className="line-clamp-1 text-sm font-medium">{title}</CardTitle>
            <CardDescription className="line-clamp-1">{domain}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="px-4 py-1">
          <p className="line-clamp-2 text-xs text-muted-foreground">{content}</p>
        </CardContent>
        <CardFooter className="flex items-center justify-between pt-0 p-2">
          <RelevanceValue value={relevancePercentage} key={idx} />
          <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </CardFooter>
      </a>
    </Card>
  );
};

interface DocumentCardProps {
  title: string;
  content?: string;
  relevanceValue: number;
  groupedCards?: CardData[];
  segmentTimestamps: {
    text: string;
    endInSeconds: number;
    startInSeconds: number; 
  }[];
  sourceLink: string | undefined;
  handleCardClick: () => void;
}

const DocumentCard = ({
  title,
  content = "",
  relevanceValue,
  groupedCards = [],
  sourceLink,
  handleCardClick
}: DocumentCardProps) => {
  function getHighestRelevance() {
    if (!groupedCards || groupedCards.length <= 1) return relevanceValue;
    
    return Math.max(
      relevanceValue,
      ...groupedCards.map(card => {
        const catMatch = card.category.match(/Relevance: ([\d.]+)/);
        if (catMatch && catMatch[1]) {
          return Math.round(parseFloat(catMatch[1]) * 100);
        }
        return card.pointOfInterest || 0;
      })
    );
  }

  const displayTitle = title;
  const highestRelevanceValue = getHighestRelevance();
  const id = Math.random().toString(36).substring(7);
  const { t } = useTranslation();

  return (
    <Card 
      key={id} 
      className="group overflow-hidden transition-all duration-300 hover:shadow-sm px-2 rounded-md bg-background/50 hover:cursor-pointer"
      onClick={handleCardClick}
    >
      <CardHeader className="flex flex-row items-center gap-2 pb-1 p-2">
        <div className="relative h-5 w-5 overflow-hidden rounded-sm mt-1">
          <FileIcon fileName={title} className="h-full w-full object-contain" />
        </div>
        <div className="flex-1">
          <CardTitle className="line-clamp-1 text-sm font-medium">{displayTitle}</CardTitle>
        </div>
        {sourceLink && sourceLink.includes("https://") && (
        <Badge variant="secondary" className="text-[10px] px-2 py-0.5 gap-1">
          <a
            href={sourceLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-x-2"
            onClick={(e) => e.stopPropagation()}
          >
            {t(tokens.workstation.sources.goToSource)}
            <ExternalLink size={12} className="text-muted-foreground" />
          </a>
        </Badge>
      )}
      </CardHeader>
      <CardContent className="p-1 pt-0">
        <p className="line-clamp-2 text-xs text-muted-foreground pl-3">{content}</p>
      </CardContent>
      <CardFooter className="flex items-center justify-between pt-0 p-2">
        {groupedCards.length > 0 && (
          <div className="flex items-center gap-x-1 flex-wrap">
            {highestRelevanceValue > 0 && (
              <RelevanceValue value={relevanceValue} key={id} />
            )}
            <Badge variant="outline" className="text-xs">
              {t(tokens.workstation.sources.passage_in_documents, { count: groupedCards.length })}
            </Badge>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};

type FlexibleCardProps = 
  | ({ type: "web" } & WebCardProps)
  | ({ type?: "document" } & DocumentCardProps);

export function FlexibleCard(props: FlexibleCardProps) {
  const { type = "document" } = props;

  if (type === "web") {
    return <WebCard {...props as WebCardProps} />;
  }

  return <DocumentCard {...props as DocumentCardProps} />;
}
